/**
 * Helper utilities for working with Orchard merkle paths.
 */

export const ORCHARD_MERKLE_DEPTH = 32;

const KNOWN_ARRAY_KEYS = ['siblings', 'path', 'hashes', 'values', 'nodes', 'elements'];

const sanitiseSiblingHex = (value: any): string => {
	if (typeof value !== 'string') return '';
	let hex = value.trim();
	if (!hex) return '';
	if (hex.startsWith('0x') || hex.startsWith('0X')) {
		hex = hex.slice(2);
	}
	hex = hex.replace(/[^0-9a-fA-F]/g, '');
	if (!hex) return '';
	if (hex.length > 64) {
		hex = hex.slice(-64);
	}
	return hex.padStart(64, '0');
};

const asArray = (candidate: any): any[] => {
	if (Array.isArray(candidate)) return candidate;
	if (candidate && typeof candidate === 'object') {
		for (const key of KNOWN_ARRAY_KEYS) {
			if (Array.isArray(candidate[key])) {
				return candidate[key];
			}
		}
	}
	return [];
};

/**
 * Extract a list of merkle path siblings from a note or raw merkle path object.
 */
export const extractMerklePathSiblings = (source?: any): string[] => {
	if (!source) return [];

	const candidates: any[] = [];

	if (Array.isArray(source)) {
		candidates.push(source);
	} else {
		if (source.merklePathSiblingsHex) candidates.push(source.merklePathSiblingsHex);
		if (source.merkle_path_siblings) candidates.push(source.merkle_path_siblings);
		if (source.merkle_path) candidates.push(source.merkle_path);
		if (source.merklePath) candidates.push(source.merklePath);
		if (source.witness?.merkle_path) candidates.push(source.witness.merkle_path);
		if (source.witness?.merklePath) candidates.push(source.witness.merklePath);
		// direct array on source
		candidates.push(source);
	}

	for (const candidate of candidates) {
		const arr = asArray(candidate);
		if (arr.length) {
			const sanitised = arr.map(sanitiseSiblingHex).filter(Boolean);
			if (sanitised.length) {
				return sanitised;
			}
		}
	}

	return [];
};

/**
 * Determine whether a note contains a complete witness (anchor + leaf position + 32 siblings)
 */
export const hasFullMerklePathWitness = (note: any): boolean => {
	if (!note) return false;
	const siblings = extractMerklePathSiblings(note);
	const leafPosition =
		note.leaf_position ??
		note.leafPosition ??
		note.leafPos ??
		note.note_position ??
		note.position;
	const anchorRoot = note.anchor_root ?? note.anchorRootHex ?? note.anchor;
	return Boolean(anchorRoot) && leafPosition !== undefined && siblings.length === ORCHARD_MERKLE_DEPTH;
};


